import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  FlagDetail,
  SbcApiService,
  ViolationFlag,
} from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';
import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { StatusBadgeComponent } from '../components/status-badge.component';

type CompareRow = {
  label: string;
  planValue: string;
  measuredValue: string;
  unit: string;
};

@Component({
  selector: 'app-officer-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './officer-dashboard.component.html',
  styleUrl: './officer-dashboard.component.css',
})
export class OfficerDashboardComponent {
  private readonly api = inject(SbcApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly session = inject(SessionService);

  flags = signal<ViolationFlag[]>([]);
  selectedId = signal<number | null>(null);
  detail = signal<FlagDetail | null>(null);
  compareRows = signal<CompareRow[]>([]);
  statusFilter = signal('open');
  comment = signal('');
  loading = signal(false);
  error = signal('');
  toast = signal('');
  compareOpen = signal(false);
  historyOpen = signal(false);

  readonly listPanelTitle = computed(() => {
    const filter = this.statusFilter();
    const titles: Record<string, string> = {
      open: 'Open cases',
      under_review: 'Under review',
      accepted: 'Accepted cases',
      rejected: 'Rejected cases',
      recheck_requested: 'Recheck requested',
      '': 'All cases',
    };
    return titles[filter] || 'Cases';
  });

  readonly openCaseCount = computed(
    () => this.flags().filter((f) => this.canReview(f.statusCd)).length,
  );

  readonly selectedFlag = computed(() => {
    const id = this.selectedId();
    if (id == null) return null;
    return this.flags().find((f) => f.id === id) ?? null;
  });

  constructor() {
    effect(() => {
      this.session.authorityId();
      untracked(() => {
        this.clearSelection();
        this.reload();
      });
    });
  }

  reload(options?: { selectFlagId?: number | null; preferNextOpen?: boolean }): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listFlags(this.statusFilter() || undefined).subscribe({
      next: (res) => {
        const rows = res.data || [];
        this.flags.set(rows);
        this.loading.set(false);

        const fromQuery = Number(this.route.snapshot.queryParamMap.get('flagId'));
        let nextId = options?.selectFlagId ?? (fromQuery > 0 ? fromQuery : this.selectedId());

        if (options?.preferNextOpen) {
          const pending = rows.find((f) => this.canReview(f.statusCd));
          nextId = pending?.id ?? null;
        } else if (nextId != null && !rows.some((f) => f.id === nextId)) {
          nextId = rows.find((f) => this.canReview(f.statusCd))?.id ?? null;
        }

        if (nextId != null && nextId > 0) {
          this.openFlag(nextId);
        } else {
          this.clearSelection();
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(
          err?.error?.message ||
            err.message ||
            'Unable to load flags. Confirm the API service is running.'
        );
      },
    });
  }

  openFlag(id: number): void {
    this.selectedId.set(id);
    this.compareOpen.set(false);
    this.historyOpen.set(false);
    this.compareRows.set([]);
    this.api.getFlag(id).subscribe({
      next: (res) => {
        this.detail.set(res.data);
        this.compareRows.set(this.buildCompareRows(res.data));
      },
      error: (err) => this.showToast(err?.error?.message || 'Unable to load case details'),
    });
  }

  onFlagSelect(raw: number | string | null): void {
    if (raw == null || raw === '') {
      this.clearSelection();
      return;
    }
    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) {
      this.clearSelection();
      return;
    }
    if (id === this.selectedId()) return;
    this.openFlag(id);
  }

  flagOptionLabel(f: ViolationFlag): string {
    return `Khasra ${f.plotRef} · ${this.statusLabel(f.statusCd)} · ${this.friendlySummary(f.summary)}`;
  }

  canReview(statusCd?: string): boolean {
    return ['open', 'under_review', 'recheck_requested'].includes(statusCd || '');
  }

  toggleCompare(): void {
    this.compareOpen.update((v) => !v);
  }

  toggleHistory(): void {
    this.historyOpen.update((v) => !v);
  }

  submit(decision: 'accept' | 'reject' | 'recheck'): void {
    const id = this.selectedId();
    if (id == null) return;
    const plotRef = this.detail()?.flag.plotRef;
    this.api
      .reviewFlag(id, { decision, comment: this.comment().trim() || null })
      .subscribe({
        next: () => {
          const label = this.decisionLabel(decision);
          const plotLabel = plotRef ? `Khasra ${plotRef}` : 'Case';
          this.showToast(`${plotLabel} — ${label}. Case updated.`);
          this.comment.set('');
          void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { flagId: null },
            queryParamsHandling: 'merge',
          });

          const closed = decision === 'accept' || decision === 'reject';
          if (closed && this.statusFilter() === '') {
            this.statusFilter.set('open');
          }
          this.clearSelection();
          this.reload({ preferNextOpen: true });
        },
        error: (err) => this.showToast(err?.error?.message || 'Unable to save decision'),
      });
  }

  isClosedCase(statusCd?: string): boolean {
    return statusCd === 'accepted' || statusCd === 'rejected';
  }

  statusLabel(status?: string): string {
    const map: Record<string, string> = {
      open: 'Open',
      under_review: 'Under review',
      accepted: 'Accepted',
      rejected: 'Rejected',
      recheck_requested: 'Recheck requested',
    };
    return map[status || ''] || status || '—';
  }

  decisionLabel(decision?: string): string {
    const map: Record<string, string> = {
      accept: 'Accepted',
      reject: 'Rejected',
      recheck: 'Recheck requested',
    };
    return map[decision || ''] || decision || '—';
  }

  friendlySummary(summary?: string): string {
    if (!summary) return 'Compliance deviation';
    return summary
      .split(',')
      .map((s) => s.trim())
      .map((code) => {
        const labels: Record<string, string> = {
          FAR_MAX: 'FAR exceeded',
          HEIGHT_MAX: 'Height exceeded',
          SETBACK_FRONT: 'Front setback shortfall',
          PLAN_SETBACK_FRONT: 'Plan setback mismatch',
          PLAN_HEIGHT: 'Plan height mismatch',
          GROUND_COVERAGE_MAX: 'Ground coverage exceeded',
        };
        return labels[code] || code;
      })
      .join(' · ');
  }

  refLabel(mode?: string): string {
    if (mode === 'plan_and_bye_law') return 'Plan + bye-law';
    if (mode === 'bye_law_only') return 'Bye-law only';
    return mode || '—';
  }

  sourceLabel(source?: string): string {
    if (source === 'bye_law') return 'Bye-law';
    if (source === 'plan') return 'Sanctioned plan';
    return source || '—';
  }

  confidencePct(value?: string | number): string {
    if (value == null || value === '') return '—';
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return `${Math.round(n * 100)}%`;
  }

  confidenceRingPct(value?: string | number): number {
    if (value == null || value === '') return 0;
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    return Math.min(100, Math.max(0, Math.round(n * 100)));
  }

  private buildCompareRows(detail: FlagDetail): CompareRow[] {
    const dims = (detail.plan?.['dimensionsJson'] || {}) as Record<string, unknown>;
    const m = detail.measurement || {};
    const fmt = (v: unknown) =>
      v == null || v === '' || Number.isNaN(Number(v)) ? '—' : String(v);

    return [
      {
        label: 'Front setback',
        planValue: fmt(dims['frontSetbackM']),
        measuredValue: fmt(m['setbackFrontM']),
        unit: 'm',
      },
      {
        label: 'Height',
        planValue: fmt(dims['heightM']),
        measuredValue: fmt(m['heightM']),
        unit: 'm',
      },
      {
        label: 'FAR',
        planValue: fmt(dims['far']),
        measuredValue: fmt(m['farValue']),
        unit: '',
      },
      {
        label: 'Ground coverage',
        planValue: fmt(dims['groundCoverage']),
        measuredValue: fmt(m['groundCoverage']),
        unit: '',
      },
    ];
  }

  private clearSelection(): void {
    this.selectedId.set(null);
    this.detail.set(null);
    this.compareRows.set([]);
  }

  private showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 3200);
  }
}
